import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  InputAdornment,
  Chip,
  SelectChangeEvent,
  FormHelperText
} from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { Pool, PoolUpdate } from '../../types/domain';
import standardPoolApi from '../../services/standardPoolApi';
import axios from 'axios';
import { API_URL } from '../../utils/api';
import { axiosInstance } from '../../services/axiosInstance';
import api from '../../services/api';
import PoolForm, { POOL_DETECTOR_COMMON_LABEL } from './PoolForm';
import PoolGatewayWebrtcStatusCard from './PoolGatewayWebrtcStatusCard';

// 상수 정의
type PoolStatusString = 'OPERATING' | 'CLOSED' | 'MAINTENANCE' | 'ALERT' | 'ERROR' | 'active' | 'inactive';
type PoolTypeString = 'INDOOR' | 'OUTDOOR';

const POOL_STATUSES: PoolStatusString[] = ['OPERATING', 'CLOSED', 'MAINTENANCE', 'ALERT', 'ERROR', 'active', 'inactive'];
const POOL_TYPES: PoolTypeString[] = ['INDOOR', 'OUTDOOR'];

interface PoolInfoManagementProps {
  poolId: number | string | undefined;
  poolCode: string | undefined;
  pool: Pool | null;
  showSnackbar: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
  onRefreshData?: () => void;
  /** 관제센터 관리자만 true. 수영장 현장(/POOL001 등)에서는 정보 수정 숨김 */
  allowInfoEdit?: boolean;
}

const PoolInfoManagement: React.FC<PoolInfoManagementProps> = ({
  poolId,
  poolCode,
  pool,
  showSnackbar,
  onRefreshData,
  allowInfoEdit = true,
}) => {
  // 상태
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false);
  const [editFormData, setEditFormData] = useState<Partial<PoolUpdate>>({});
  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleGeneratePairingCode = async () => {
    if (!poolId) return;
    try {
      setIsLoading(true);
      const res = await axios.post(
        `${API_URL}/api/pools/${poolId}/gateway/pairing-code`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` } }
      );
      showSnackbar(`페어링 코드: ${res.data.pairing_code}`, 'success');
      onRefreshData?.();
    } catch (e: any) {
      showSnackbar(e?.response?.data?.detail || '페어링 코드 발급 실패', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // 시설 수정 다이얼로그 열기
  const handleOpenEditDialog = () => {
    if (!allowInfoEdit) {
      showSnackbar('시설 정보 수정은 관제센터에서만 가능', 'warning');
      return;
    }
    if (!pool) {
      showSnackbar('시설 정보를 찾기 불가', 'error');
      return;
    }

    setEditFormData({
      name: pool.name,
      location: pool.location,
      address: pool.address,
      capacity: pool.capacity,
      current_occupancy: pool.current_occupancy,
      manager_name: pool.manager_name,
      manager_phone: pool.manager_phone,
      safety_phone: pool.safety_phone,
      email: pool.email,
      type: pool.type,
      status: pool.status,
      code: pool.code,
      password: pool.password,
      is_active: pool.is_active,
      site_gateway_enabled: pool.site_gateway_enabled ?? false,
      site_monitor_prefer_local: pool.site_monitor_prefer_local ?? false,
      monitor_zone_tiles_only: pool.monitor_zone_tiles_only ?? false,
    });
    
    setFormErrors({});
    setIsEditDialogOpen(true);
  };

  // 시설 수정 다이얼로그 닫기
  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setEditFormData({});
    setFormErrors({});
  };

  // 필드 변경 핸들러
  const handleEditPoolChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | { name?: string; value: unknown }>) => {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement | { name?: string; value: unknown };
    const name = target.name as string;
    const value = target.value;

    if (name) {
      if (target instanceof HTMLInputElement && target.type === 'checkbox') {
        setEditFormData(prev => ({ ...prev, [name]: target.checked }));
      } else {
        const numericFields = ['capacity', 'current_occupancy', 'clean_cycle'];
        if (numericFields.includes(name)) {
          const parsedValue = value === '' ? null : parseInt(value as string, 10);
          setEditFormData(prev => ({ ...prev, [name]: isNaN(parsedValue as number) ? null : parsedValue }));
        } else {
          setEditFormData(prev => ({ ...prev, [name]: value }));
        }
      }

      // 에러 필드 클리어
      if (formErrors[name]) {
        setFormErrors(prev => ({ ...prev, [name]: false }));
      }
    }
  };

  // 셀렉트 변경 핸들러
  const handleEditPoolSelectChange = (event: SelectChangeEvent) => {
    const { name, value } = event.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
    
    // 에러 필드 클리어
    if (formErrors[name as string]) {
      setFormErrors(prev => ({ ...prev, [name as string]: false }));
    }
  };

  // 비밀번호 표시 토글
  const handleTogglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  // 폼 유효성 검사
  const validatePoolForm = (): boolean => {
    const errors: Record<string, boolean> = {};
    
    // 필수 필드 확인
    if (!editFormData.name) errors.name = true;
    if (!editFormData.location) errors.location = true;
    if (!editFormData.code) errors.code = true;
    if (!editFormData.type) errors.type = true;
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 시설 정보 수정 제출
  const handleEditPoolSubmit = async () => {
    if (!validatePoolForm()) {
      showSnackbar('필수 입력 항목을 확인', 'warning');
      return;
    }

    if (!pool || !poolCode) {
      showSnackbar('시설 정보를 찾기 불가', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const updateData: PoolUpdate = {
        ...editFormData,
        id: pool.id
      };

      // API를 직접 호출합니다
      console.log(`[PoolInfoManagement] 시설 코드(${poolCode})로 직접 API 호출`);
      console.log(`[PoolInfoManagement] 요청 데이터:`, JSON.stringify(updateData, null, 2));
      
      await axiosInstance.put(`/api/pools/code/${poolCode}`, updateData);
      console.log(`[PoolInfoManagement] 시설 정보 업데이트 성공`);
      showSnackbar('시설 정보가 수정됨', 'success');
      handleCloseEditDialog();
      if (onRefreshData) {
        onRefreshData();
      }
    } catch (error: any) {
      console.error('[PoolInfoManagement] 시설 정보 수정 중 에러:', error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || '알 수 없는 오류';
      showSnackbar(`시설 정보 수정 중 오류: ${errorMessage}`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 상태 칩 렌더링
  const renderStatusChip = (status: string | undefined) => {
    let color: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" = "default";
    let label = status?.toUpperCase() || "UNKNOWN";

    switch (label) {
      case 'ACTIVE':
      case 'OPERATING':
        color = "success";
        label = '운영중';
        break;
      case 'INACTIVE':
      case 'CLOSED':
        color = "default";
        label = '중지됨';
        break;
      case 'MAINTENANCE':
        color = "warning";
        label = '점검중';
        break;
      case 'ALERT':
        color = "warning";
        label = '경고';
        break;
      case 'ERROR':
        color = "error";
        label = '오류';
        break;
      default:
        color = "default";
        label = status || '알수없음';
    }
    return <Chip label={label} color={color} size="small" sx={{ minWidth: 70 }}/>;
  };

  // 시설 기본 정보 렌더링
  const renderPoolInfo = () => {
    if (isLoading) {
      return <CircularProgress />;
    }
    
    if (error) {
      return <Alert severity="error">{error}</Alert>;
    }
    
    if (!pool) {
      return <Alert severity="info">시설 정보를 찾을 수 없음.</Alert>;
    }
    
    return (
      <Paper sx={{ p: 3, mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">기본 정보</Typography>
          {allowInfoEdit ? (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleOpenEditDialog}
            >
              정보 수정
            </Button>
          ) : null}
        </Box>
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">이름</Typography>
            <Typography variant="body1">{pool.name || '-'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">코드</Typography>
            <Typography variant="body1">{pool.code || '-'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">위치</Typography>
            <Typography variant="body1">{pool.location || '-'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">주소</Typography>
            <Typography variant="body1">{pool.address || '-'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">타입</Typography>
            <Typography variant="body1">
              {pool.type === 'INDOOR' ? '실내' : pool.type === 'OUTDOOR' ? '실외' : pool.type || '-'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">상태</Typography>
            <Box sx={{ mt: 0.5 }}>
              {renderStatusChip(pool.status)}
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">수용 인원</Typography>
            <Typography variant="body1">
              {pool.capacity !== undefined && pool.capacity !== null ? `${pool.capacity}명` : '-'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">현재 이용 인원</Typography>
            <Typography variant="body1">
              {pool.current_occupancy !== undefined && pool.current_occupancy !== null ? `${pool.current_occupancy}명` : '-'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">라이브 (현장 게이트웨이)</Typography>
            <Typography variant="body1">
              {pool.site_gateway_enabled
                ? 'ON · 라이브 (PoolGateway / go2rtc)'
                : 'OFF · 목업 (stream_url · 예: :8500 HLS)'}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {pool.site_gateway_enabled
                ? '현장 라이브 스트림으로 관제·AI·증거(pre/post)를 씀. D:\\hls_stream 목업은 사용하지 않음.'
                : '개발 목업: 중앙이 stream_url에 직접 접속. 증거는 중앙 롤링 HLS(실시간 페이스)로 수집.'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">
              시설 관제 모니터 소스
            </Typography>
            <Typography variant="body1">
              {pool.site_monitor_prefer_local
                ? '현장 RTSP/직접 (stream_url)'
                : '중앙 게이트웨이 (관제실과 동일)'}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              시설 /{`{code}`} 관제에만 적용. 관제실(?key)은 항상 게이트웨이 경로 유지.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">
              관제 화면 CCTV 표시
            </Typography>
            <Typography variant="body1">
              {pool.monitor_zone_tiles_only
                ? '감시구역 박스만 (구역 크롭 타일)'
                : 'CCTV 전체 화면'}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              시설 수정에서 토글. 중앙 관제 격자에 바로 반영.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">현장 PC 입력 — 시설 코드</Typography>
            <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
              {pool.code || '—'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">전송 화질 (현장 게이트웨이)</Typography>
            <Typography variant="body1">
              {pool.gateway_stream_managed
                ? pool.gateway_nvr_bypass
                  ? '이 시설 설정 · 원본(copy)'
                  : `이 시설 설정 · ${pool.gateway_stream_width || 640}px · ${pool.gateway_stream_fps ?? 8}fps`
                : '현장 화면 값 유지'}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              이 시설 설정이 켜져 있으면 현장 화면보다 우선. 약 30초 안에 현장 게이트웨이에 반영.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">HLS 중앙 업로드</Typography>
            <Typography variant="body1">
              {pool.gateway_hls_upload === false ? 'OFF' : 'ON'}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              OFF여도 관제 WebRTC·증거(링버퍼)는 유지. 업링크 절약용.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">시설 NVR</Typography>
            <Typography variant="body1">
              {pool.nvr_host
                ? `${pool.nvr_host}:${pool.nvr_rtsp_port ?? 554} · ${pool.nvr_vendor || 'hikvision'} · ${
                    pool.nvr_password_set ? '비밀번호 저장됨' : '비밀번호 없음'
                  }`
                : '미설정'}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              라벨링용 지난 영상. 현장 게이트웨이가 LAN으로 접속.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">게이트웨이 페어링 코드</Typography>
            <Typography variant="body1">{pool.gateway_pairing_code || '—'}</Typography>
            <Button size="small" sx={{ mt: 1 }} onClick={handleGeneratePairingCode} disabled={isLoading}>
              코드 발급/재발급
            </Button>
          </Grid>
          {allowInfoEdit ? (
            <Grid item xs={12}>
              <PoolGatewayWebrtcStatusCard poolId={poolId} visible={allowInfoEdit} />
            </Grid>
          ) : null}
        </Grid>
        
        <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>연락처 정보</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">관리자 이름</Typography>
            <Typography variant="body1">{pool.manager_name || '-'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">관리자 연락처</Typography>
            <Typography variant="body1">{pool.manager_phone || '-'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">안전 연락처</Typography>
            <Typography variant="body1">{pool.safety_phone || '-'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">이메일</Typography>
            <Typography variant="body1">{pool.email || '-'}</Typography>
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
          영상 AI 감지 (CCTV 모니터링)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          실시간 사람 검출(YOLO)에 사용. 카메라마다 각도·물결이 다르므로 보통은 CCTV 관리에서 카메라별로 조정하고, 여기 값은 그 카메라에서 비워 두었을 때만 적용되는 시설 기본값. yolov8n 물놀이장 권장(균형)은 신뢰도 약 0.30.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">검출 신뢰도 하한 (권장 0.30)</Typography>
            <Typography variant="body1">
              {pool.ai_detection_conf != null && pool.ai_detection_conf !== undefined
                ? String(pool.ai_detection_conf)
                : '0.30 (시스템·시설 기본)'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">연속 프레임 확정 (기본 3)</Typography>
            <Typography variant="body1">
              {pool.ai_detection_min_consecutive_hits != null &&
              pool.ai_detection_min_consecutive_hits !== undefined
                ? `${pool.ai_detection_min_consecutive_hits}회`
                : '3회 (시스템 기본)'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">전용 검출 모델</Typography>
            <Typography variant="body1">
              {pool.ai_detector_model && String(pool.ai_detector_model).trim()
                ? String(pool.ai_detector_model).trim()
                : POOL_DETECTOR_COMMON_LABEL}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">추론 파이프라인</Typography>
            <Typography variant="body1">
              {(() => {
                const p = String(pool.yolo_pipeline_preset ?? '').trim();
                if (!p) return '전역 설정 사용';
                if (p === 'dedicated') return '전용 PT 단독';
                if (p === 'hybrid_recall') return '전용 + COCO 리콜 보조';
                if (p === 'coco_only_debug') return 'COCO만 (디버그)';
                return p;
              })()}
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  // 시설 수정 다이얼로그 렌더링
  const renderEditDialog = () => {
    if (!allowInfoEdit) return null;
    return (
      <PoolForm
        open={isEditDialogOpen}
        onClose={handleCloseEditDialog}
        onSubmit={async (data) => {
          if (!pool || !poolCode) {
            showSnackbar('시설 정보를 찾기 불가', 'error');
            return;
          }
          setIsSubmitting(true);
          try {
            const ai =
              (data as { _aiDetectionSave?: {
                ai_detection_conf: number;
                ai_detection_min_consecutive_hits: number;
                ai_detector_model: string | null;
                yolo_pipeline_preset: string | null;
              } })._aiDetectionSave || {
                ai_detection_conf: Number(data.ai_detection_conf),
                ai_detection_min_consecutive_hits: Number(
                  data.ai_detection_min_consecutive_hits
                ),
                ai_detector_model:
                  data.ai_detector_model != null
                    ? String(data.ai_detector_model).trim() || null
                    : null,
                yolo_pipeline_preset:
                  (data as { yolo_pipeline_preset?: string | null }).yolo_pipeline_preset != null
                    ? String(
                        (data as { yolo_pipeline_preset?: string | null }).yolo_pipeline_preset
                      ).trim() || null
                    : null,
              };

            // 1) 검출 성향(conf/hits)만 단독 PUT — 다른 필드와 절대 섞지 않음
            await axiosInstance.put(`/api/pools/code/${poolCode}`, {
              ai_detection_conf: Number(ai.ai_detection_conf),
              ai_detection_min_consecutive_hits: Number(
                ai.ai_detection_min_consecutive_hits
              ),
            });

            // 2) DB 재조회로 검증 (실패 시 여기서 throw → 다이얼로그 유지)
            api.invalidateGetCache('/api/pools');
            const verified = await axiosInstance.get(`/api/pools/code/${poolCode}`, {
              params: { _: Date.now() },
            });
            const v = verified.data || {};
            const gotConf = Number(v.ai_detection_conf);
            const gotHits = Number(v.ai_detection_min_consecutive_hits);
            if (
              !Number.isFinite(gotConf) ||
              Math.abs(gotConf - Number(ai.ai_detection_conf)) > 0.001 ||
              gotHits !== Number(ai.ai_detection_min_consecutive_hits)
            ) {
              throw new Error(
                `검출 성향이 DB에 반영되지 않음 (요청 ${ai.ai_detection_conf}/${ai.ai_detection_min_consecutive_hits}, 실제 ${v.ai_detection_conf}/${v.ai_detection_min_consecutive_hits})`
              );
            }

            // 3) 모델·파이프라인 (선택) — 성향과 분리
            await axiosInstance.put(`/api/pools/code/${poolCode}`, {
              ai_detector_model: ai.ai_detector_model,
              yolo_pipeline_preset: ai.yolo_pipeline_preset,
            });

            // 4) 나머지 시설 정보 (AI 필드 제외)
            const updateData = { ...data, id: pool.id };
            delete (updateData as { _aiDetectionSave?: unknown })._aiDetectionSave;
            delete (updateData as { ai_detection_conf?: unknown }).ai_detection_conf;
            delete (updateData as { ai_detection_min_consecutive_hits?: unknown })
              .ai_detection_min_consecutive_hits;
            delete (updateData as { ai_detector_model?: unknown }).ai_detector_model;
            delete (updateData as { yolo_pipeline_preset?: unknown }).yolo_pipeline_preset;
            await axiosInstance.put(`/api/pools/code/${poolCode}`, updateData);

            api.invalidateGetCache('/api/pools');
            showSnackbar(
              `저장됨 — 검출 신뢰도 ${gotConf}, 연속 ${gotHits}회`,
              'success'
            );
            handleCloseEditDialog();
            if (onRefreshData) onRefreshData();
          } catch (error: any) {
            const detail = error.response?.data?.detail;
            const errorMessage =
              (typeof detail === 'string' ? detail : null) ||
              (Array.isArray(detail)
                ? detail.map((d: { msg?: string }) => d?.msg).filter(Boolean).join('; ')
                : null) ||
              error.response?.data?.message ||
              error.message ||
              '알 수 없는 오류';
            showSnackbar(`시설 정보 수정 중 오류: ${errorMessage}`, 'error');
            // PoolForm이 다이얼로그를 닫지 않도록 반드시 재throw
            throw error;
          } finally {
            setIsSubmitting(false);
          }
        }}
        pool={pool}
        poolId={poolId}
      />
    );
  };

  // 메인 렌더링
  return (
    <Box>
      {renderPoolInfo()}
      {renderEditDialog()}
    </Box>
  );
};

export default PoolInfoManagement; 